import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import axiosClient from "../../axiosClient";
import { platform } from "../../brand";
import Button from "../Button/Button";
import type { Product } from "../../data/productTypes";

/** Products that can be bought on this brand. */
export function getPurchasableProducts(products: readonly Product[]): Product[] {
  return products.filter((p) => !p.comingSoon && p.purchasable !== false && p.showOnSignUp !== false);
}

/** Purchasable products that can also be started as a free trial. */
export function getTrialableProducts(products: readonly Product[]): Product[] {
  return getPurchasableProducts(products).filter((p) => p.trialable === true);
}

export function getComingSoonProducts(products: readonly Product[]): Product[] {
  return products.filter((p) => p.comingSoon && p.showOnSignUp !== false);
}

/**
 * Product cards used on sign-up, free-trial and upgrade pages.
 * Styles live in SignUpPage.scss, so render inside an element with id="SignUpPage".
 */
export function ProductPicker(props: {
  products: readonly Product[];
  selected: Set<string>;
  onChange: (selected: Set<string>) => void;
  multiple: boolean;
  /** Shows the full price struck through, followed by the free trial terms. */
  trialOffer?: { days: number; questions: number };
  /** Products the user already has full access to: shown, but can't be picked. */
  ownedProductIds?: ReadonlySet<string>;
  /** Products whose free trial has ended: labelled, but still selectable so they can be bought. */
  trialledProductIds?: ReadonlySet<string>;
  /** Products the user previously bought whose access has expired: labelled, and selectable to renew. */
  renewableProductIds?: ReadonlySet<string>;
}) {
  const {
    products,
    selected,
    onChange,
    multiple,
    trialOffer,
    ownedProductIds,
    trialledProductIds,
    renewableProductIds,
  } = props;

  const toggle = (productId: string) => {
    if (ownedProductIds?.has(productId)) {
      return;
    }
    if (!multiple) {
      onChange(new Set([productId]));
      return;
    }
    const next = new Set(selected);
    if (next.has(productId)) {
      next.delete(productId);
    } else {
      next.add(productId);
    }
    onChange(next);
  };

  return (
    <>
      {products.map((product) => {
        const owned = ownedProductIds?.has(product.id) ?? false;
        let className = "oq-product-card-wrapper";
        if (owned) {
          className += " oq-product-card-wrapper-owned";
        } else if (selected.has(product.id)) {
          className += " oq-product-card-wrapper-selected";
        }

        return (
          <div key={product.id}>
            <div
              className={className}
              aria-disabled={owned || undefined}
              onClick={(e) => {
                // Cancelling a click on the checkbox (or its label) would undo the tick, so they use onChange.
                if ((e.target as HTMLElement).closest("input, label")) {
                  return;
                }
                e.stopPropagation();
                toggle(product.id);
              }}
            >
              <div>
                <input
                  name={product.id}
                  id={product.id}
                  type="checkbox"
                  checked={!owned && selected.has(product.id)}
                  disabled={owned}
                  onChange={() => toggle(product.id)}
                  className="oq-product-card-checkbox"
                />
                <label htmlFor={product.id}>
                  {product.title}
                  {!owned &&
                    (trialOffer ? (
                      <>
                        :{" "}
                        <s className="oq-product-card-full-price">
                          £{product.price} / {product.length}
                        </s>{" "}
                        <span>£0</span>
                        <small className="oq-product-card-trial-terms">
                          Free for {trialOffer.days} days, {trialOffer.questions} questions
                        </small>
                      </>
                    ) : (
                      <>
                        : <span>£{product.price}</span> / {product.length}
                      </>
                    ))}
                </label>
              </div>
              {owned ? (
                <span className="oq-product-card-owned-badge">Already purchased</span>
              ) : renewableProductIds?.has(product.id) ? (
                <span className="oq-product-card-renew-badge">Renew</span>
              ) : trialledProductIds?.has(product.id) ? (
                <span className="oq-product-card-trialled-badge">Trialled</span>
              ) : (
                <div></div>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
}

export type AppliedCoupon = {
  code: string;
  totalAfter: number;
  discountAmount: number;
  details?: any;
};

/** Coupon state shared by checkout pages; clears the coupon whenever the product selection changes. */
export function useCoupon(params: {
  products: readonly Product[];
  selectedProducts: Set<string>;
  getEmail: () => string;
}) {
  const { products, selectedProducts, getEmail } = params;
  const [couponInput, setCouponInput] = useState<string>("");
  const [isApplyingCoupon, setIsApplyingCoupon] = useState<boolean>(false);
  const [appliedCoupon, setAppliedCoupon] = useState<AppliedCoupon | null>(null);

  const totalPrice = () => {
    return products
      .filter((product) => selectedProducts.has(product.id))
      .reduce((sum, product) => sum + product.price, 0);
  };

  const handleApplyCoupon = async (e?: any) => {
    if (e) e.preventDefault();
    const code = couponInput.trim();
    if (!code) return;
    if (selectedProducts.size === 0) {
      toast.error("Please select at least one product first.");
      return;
    }
    const email = getEmail();
    if (!email) {
      toast.error("Please enter your email before applying a coupon.");
      return;
    }
    try {
      setIsApplyingCoupon(true);
      const r = await axiosClient.post("payments/oq-validate-coupon", {
        code,
        products: Array.from(selectedProducts),
        email,
        platform,
      });
      const data = r.data || {};
      if (data.valid === false) {
        setAppliedCoupon(null);
        toast.error(data?.error || "Invalid coupon");
        return;
      }
      const subtotal = totalPrice();
      const totalAfter = typeof data.totalAfter === "number" ? data.totalAfter : subtotal;
      const discountAmount = Math.max(0, subtotal - totalAfter);
      if (discountAmount <= 0) {
        setAppliedCoupon(null);
        toast.error("Coupon does not apply to current selection.");
        return;
      }
      setAppliedCoupon({ code, totalAfter, discountAmount, details: data });
      toast.success("Coupon applied");
    } catch (err: any) {
      console.error(err);
      setAppliedCoupon(null);
      toast.error(err?.response?.data?.error || "Could not validate coupon");
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  // Clear coupon when product selection changes to avoid stale discounts
  useEffect(() => {
    if (appliedCoupon) {
      setAppliedCoupon(null);
      // subtle notice rather than error
      if (couponInput) {
        toast("Coupon cleared because products changed");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProducts]);

  return {
    couponInput,
    setCouponInput,
    isApplyingCoupon,
    appliedCoupon,
    setAppliedCoupon,
    handleApplyCoupon,
    totalPrice,
  };
}

export function CouponSection(props: { coupon: ReturnType<typeof useCoupon> }) {
  const { couponInput, setCouponInput, isApplyingCoupon, appliedCoupon, setAppliedCoupon, handleApplyCoupon } =
    props.coupon;

  return (
    <div className="coupon-section">
      <h5>Coupon</h5>
      {!appliedCoupon && (
        <div className="coupon-row">
          <input
            className="coupon-input"
            type="text"
            placeholder="Enter coupon code"
            value={couponInput}
            onChange={(e) => setCouponInput(e.target.value)}
          />
          <div className="coupon-apply-button">
            <Button
              text={isApplyingCoupon ? "Applying…" : "Apply"}
              disabled={isApplyingCoupon || !couponInput.trim()}
              type="navy"
              onClick={handleApplyCoupon}
            />
          </div>
        </div>
      )}
      {appliedCoupon && (
        <div className="applied-coupon-row">
          <span>Applied coupon: <strong>{appliedCoupon.code}</strong></span>
          <button className="remove-coupon" type="button" onClick={() => setAppliedCoupon(null)}>Remove</button>
        </div>
      )}
    </div>
  );
}
